import React, {useEffect, useState} from "react";
import { Box, Typography, Divider, useMediaQuery, Grid, Chip, Tooltip } from "@mui/material";
import CustomTab from "components/Tabs/Tab";
import PropertyDetails from "./propertyDetails";
import axios from "axios";
import { baseURL } from "services/API";
import HouseIcon from "@mui/icons-material/House";
import FormatPaintIcon from "@mui/icons-material/FormatPaint";
import NearMeIcon from "@mui/icons-material/NearMe";
import PlaceIcon from "@mui/icons-material/Place";
import UnitsTable from "components/Tables/UnitsTable";
import Tickets from "scenes/Tickets";
import UnassignedUnitsTable from "components/Tables/UnassignedUnitsTable";
import PropertyDashboard from "./propertyDashboard";
import { useSelector } from "react-redux";
import { setCurrentTab } from "state";
import { useDispatch } from "react-redux";
import PropertyTenants from "./propertyTenants";
import PropertyManagers from "./propertyManagers";
import PastTenants from "./pastTenants";
import RentArrears from "./rentArrears";
import BulkPaymentsUpload from "components/Forms/BulkPaymentsUpload";
import SecurityDeposits from "./securityDeposits";
import RentalPayments from "./rentalPayments";
import PropertyOwnershipHistory from "./PropertyOwnershipHistory";
import BusinessIcon from "@mui/icons-material/Business";
import HistoryIcon from "@mui/icons-material/History";

const facilityIcons = {
  marginRight: "8px",
  fontSize: "20px",
  color: "#52524e",
};

const coloredIconGreen = {
  marginRight: "8px",
  fontSize: "20px",
  color: "#0af047",
};

const coloredIconRed = {
  marginRight: "8px",
  fontSize: "20px",
  color: "#f20a0a",
};

const detailsText = {
  fontSize: "16px",
  fontWeight: 600,
  padding: "5px",
};

const descriptionText = {
  fontSize: "15px",
  fontWeight: 400,
  fontStyle: "italic",
};




const OWNERSHIP_HISTORY_TAB = 12;

const PropertyDetailsTab = () => {
    const dispatch = useDispatch();
    const id = useSelector((state) => state.property_id);
    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
      setValue(newValue);
      dispatch(setCurrentTab({ name: "prropertyTab", tab: newValue }));
    };

    const goToOwnershipHistory = () => {
      setValue(OWNERSHIP_HISTORY_TAB);
      dispatch(setCurrentTab({ name: "prropertyTab", tab: OWNERSHIP_HISTORY_TAB }));
    };
    
    const [propertyDetails, setPropertyDetails] = useState({});
    const [ownershipSummary, setOwnershipSummary] = useState(null);
    const [landlordName, setLandlordName] = useState("");
    const [landlordPayDay, setLandlordPayDay] = useState("");
    const [rentalName, setRentalName] = useState("");
    const [propertyUse, setPropertyUse] = useState("");
    const [propertyLocation, setPropertyLocation] = useState("");
    const [locationCounty, setLocationCounty] = useState("");
    const [security, setSecurity] = useState("");
    const [hospitalAccess, setHospitalAccess] = useState("");
    const [roadAccess, setRoadAccess] = useState("");
    const [parking, setParking] = useState("");
    const [fibre, setFibre] = useState("");
    const [elevator, setElevator] = useState("");
    const [gym, setGym] = useState("");
    const [pool, setPool] = useState("");

    const isNonMobileScreen = useMediaQuery("(min-width:600px)");

    const fetchDetails = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/landlords/rentals/details?rental_id=${id}`
        );
        setPropertyDetails(response.data.data);
        const data = response.data.data;
        setRentalName(data.rental_name);
        setPropertyUse(data.rental_type);
        setPropertyLocation(data.location);
        setLocationCounty(data.location_county);
        setSecurity(data.has24_security);
        setHospitalAccess(data.hospital_access);
        setRoadAccess(data.main_road_access);
        setParking(data.has_parking);
        setFibre(data.has_fibre);
        setElevator(data.has_elevator);
        setGym(data.has_gym);
        setPool(data.has_swimming_pool);

        const landlord = data.related_landlord;
        if (landlord && typeof landlord === "object") {
          setLandlordName(landlord.business_name || "—");
          setLandlordPayDay(landlord.pay_day ? `Pay day: ${landlord.pay_day}` : "");
        } else if (landlord) {
          setLandlordName(`Landlord #${landlord}`);
          setLandlordPayDay("");
        } else {
          setLandlordName("—");
          setLandlordPayDay("");
        }
        setOwnershipSummary(data.ownership_summary ?? null);
      } catch (e) {
        setPropertyDetails(true);
      }
    };

    useEffect(() => {
        if (id) fetchDetails();
    }, [id]);


    const ownershipBadgeTitle = ownershipSummary?.has_multiple_owners
      ? [
          `${ownershipSummary.period_count} ownership period(s)`,
          ownershipSummary.owned_since
            ? `Current owner since ${new Date(ownershipSummary.owned_since).toLocaleDateString()}`
            : null,
          ownershipSummary.previous_landlord_name
            ? `Previous: ${ownershipSummary.previous_landlord_name}`
            : null,
          ownershipSummary.last_transfer_reason
            ? `Last transfer: ${ownershipSummary.last_transfer_reason.replace(/_/g, " ")}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

    const tabComponents = [
      { Component: <PropertyDashboard handleChange={handleChange}/>, label: "Property DashBoard", index: 0 },
      { Component: <PropertyDetails/>, label: "Property Details", index: 1 },
      { Component: <UnitsTable/>, label: "Units", index: 2 },
      { Component: <PropertyTenants property_id={id}/>, label: "Current Tenants", index: 3 },
      { Component: <UnassignedUnitsTable propertyDetails={propertyDetails}/>, label: "Unoccupied Units", index: 4 },
      { Component: <Tickets/>, label: "Tickets", index: 5 },
      { Component: <PropertyManagers/>, label: "Managers Attached", index: 6 },
      { Component: <PastTenants property_id={id}/>, label: "Past Tenants", index: 7 },
      { Component: <RentArrears/>, label: "Rent Arrears", index: 8 },
      { Component: <BulkPaymentsUpload rentalId={id} />, label: "Bulk Upload", index: 9 },
      { Component: <SecurityDeposits propertyId={id} />, label: "Security Deposits", index: 10 },
      { Component: <RentalPayments rentalId={id} />, label: "Rental Payments", index: 11 },
      { Component: <PropertyOwnershipHistory rentalId={id} />, label: "Ownership History", index: OWNERSHIP_HISTORY_TAB },
    ]; 


    return (
      <Box>
        <Box sx={{ alignItems: "baseline", marginBottom: "0", width:"80%", margin:"0 0 0 0.5rem" }}>
            <Grid container sx={{ margin: "0.7rem  0 0 0.5rem" }}>
              <Grid md={2} xs={5.8} sx={{ margin: "0.1rem" }}>
                <Box sx={{ display:"flex", flexDirection: "row" }}>
                  <HouseIcon sx={facilityIcons} />
                  <Typography sx={descriptionText}>Property Name: </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={detailsText}>{rentalName}</Typography>
                  {ownershipSummary?.has_multiple_owners && (
                    <Tooltip
                      title={
                        ownershipBadgeTitle
                          ? `${ownershipBadgeTitle} — Click to view ownership history`
                          : "Click to view ownership history"
                      }
                    >
                      <Chip
                        size="small"
                        icon={<HistoryIcon />}
                        label={`${ownershipSummary.period_count} owners`}
                        color="warning"
                        variant="outlined"
                        onClick={goToOwnershipHistory}
                        sx={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Divider orientation="vertical" variant="middle" flexItem />
              {isNonMobileScreen && 
                <>
                  <Grid md={2} xs={5.8} sx={{ margin: "0.1rem" }}>
                    <Box sx={{ display:"flex", flexDirection: "row" }}>
                      <FormatPaintIcon sx={facilityIcons} />
                      <Typography sx={descriptionText}>Property Use:</Typography>
                    </Box>
                    <Typography sx={detailsText}>{propertyUse}</Typography>
                  </Grid>
                  <Divider orientation="vertical" variant="middle" flexItem />
                  <Grid md={2} xs={5.8} sx={{ margin: "0.1rem" }}>
                    <Box sx={{ display:"flex", flexDirection: "row" }}>
                      <NearMeIcon sx={facilityIcons} />
                      <Typography sx={descriptionText}>Property Location:</Typography>
                    </Box>
                    <Typography sx={detailsText}>{propertyLocation}</Typography>
                  </Grid>
                  <Divider orientation="vertical" variant="middle" flexItem />
                </>
              }
              <Grid md={2} xs={5.8} sx={{ margin: "0.1rem" }}>
                <Box sx={{ display:"flex", flexDirection: "row" }}>
                  <PlaceIcon sx={facilityIcons} />
                  <Typography sx={descriptionText}>Location County:</Typography>
                </Box>
                <Typography sx={detailsText}>{locationCounty}</Typography>
              </Grid>
              {isNonMobileScreen && 
                <>
                  <Divider orientation="vertical" variant="middle" flexItem />
                  <Grid md={2} xs={5.8} sx={{ margin: "0.1rem" }}>
                    <Box sx={{ display:"flex", flexDirection: "row" }}>
                      <BusinessIcon sx={facilityIcons} />
                      <Typography sx={descriptionText}>Landlord:</Typography>
                    </Box>
                    <Typography sx={detailsText}>{landlordName}</Typography>
                    {landlordPayDay && (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: "5px" }}>
                        {landlordPayDay}
                      </Typography>
                    )}
                  </Grid>
                </>
              } 
            </Grid>
        </Box>

        <Divider />
        <CustomTab tabComponents={tabComponents} value={value} handleChange={handleChange}/>
      </Box>
    );

}

export default PropertyDetailsTab;